import { Link } from "react-router";
import type { Home, HomeAction } from "@hik/contracts";
import { useSession } from "../../lib/session";
import { useI18n } from "../../lib/i18n";
import { useHome } from "./api";
import { LinkButton } from "../../ui/Button";
import { Icon, type IconName } from "../../ui/Icon";
import { Status, leaveTone } from "../../ui/Status";
import { Avatar } from "../../ui/Avatar";
import { Empty, QueryView } from "../../ui/States";
import { PageHeader, Section } from "../../ui/Page";
import { BalanceMeter } from "../leave/BalanceMeter";

const ACTION_ICON: Record<HomeAction["kind"], IconName> = {
  approve_leave: "calendar-check",
  approve_expense: "receipt",
  approve_advance: "hand-coins",
  task: "list-checks",
  document_request: "upload-simple",
  fix_returned_leave: "pencil-simple",
  fix_returned_expense: "pencil-simple",
  payslip_new: "wallet",
  announcement_ack: "megaphone",
  asset_ack: "package",
  leave_record_ack: "seal-check",
};

const ACTION_CTA: Record<HomeAction["kind"], string> = {
  approve_leave: "İncele",
  approve_expense: "İncele",
  approve_advance: "İncele",
  task: "Göreve git",
  document_request: "Yükle",
  fix_returned_leave: "Düzelt",
  fix_returned_expense: "Düzelt",
  payslip_new: "Görüntüle",
  announcement_ack: "Oku",
  asset_ack: "Onayla",
  leave_record_ack: "Kontrol et",
};

function greeting(): string {
  const h = new Date().getHours();
  if (h < 11) return "Günaydın";
  if (h < 18) return "İyi günler";
  return "İyi akşamlar";
}

export function HomePage() {
  const { session } = useSession();
  const q = useHome();
  const i = useI18n();
  const first = (session?.employee?.name ?? session?.user.displayName ?? "").split(" ")[0];

  return (
    <div className="page page--home">
      <QueryView query={q} lines={6}>
        {(home) => {
          const due = home.actions.filter((a) => a.dueDate).sort((a, b) => a.dueDate!.localeCompare(b.dueDate!))[0];
          const lead =
            home.actions.length === 0
              ? "Bugün sizden beklenen bir iş yok."
              : `Bugün sizden beklenen ${home.actions.length} iş var${due ? `; biri ${i.date(due.dueDate!, "dayMonth")} tarihine kadar` : ""}.`;
          return (
            <>
              <PageHeader
                kicker={i.date(home.today, "long")}
                title={`${greeting()}, ${first}.`}
                lead={lead}
                actions={
                  session?.employee ? (
                    <>
                      <LinkButton to="/izin/yeni" variant="primary" icon="calendar-plus">
                        İzin iste
                      </LinkButton>
                      <LinkButton to="/masraf/yeni" variant="secondary" icon="receipt">
                        Masraf bildir
                      </LinkButton>
                    </>
                  ) : undefined
                }
              />
              <Actions home={home} />
              <Tracked home={home} />
              <div className="home-grid">
                {home.annualLeave && (
                  <Section title="Yıllık izin">
                    <BalanceMeter available={home.annualLeave.available} pending={home.annualLeave.pending} />
                    {home.annualLeave.nextAccrual && (
                      <p className="muted">
                        Sonraki hak ediş: {i.date(home.annualLeave.nextAccrual.date, "medium")}, +{i.amount(home.annualLeave.nextAccrual.amount, "day")}
                      </p>
                    )}
                    {home.nextLeave && (
                      <p>
                        Sıradaki izniniz: <Link to={`/izin/talepler/${home.nextLeave.id}`}>{i.range(home.nextLeave.startDate, home.nextLeave.endDate)}</Link> ({home.nextLeave.typeName})
                      </p>
                    )}
                    <p>
                      <Link to="/izin">İzin hareketlerim ve geçmişim</Link>
                    </p>
                  </Section>
                )}
                {home.team && <Team home={home} />}
              </div>
              {home.hr && <HrSummary hr={home.hr} />}
              {home.finance && (
                <Section title="Finans">
                  <ul className="link-list">
                    <li>
                      <Link to="/finans">Ödeme bekleyen masraflar: {home.finance.awaitingPayment}</Link>
                    </li>
                    <li>
                      <Link to="/onaylar">Finans onayı bekleyenler: {home.finance.awaitingApproval}</Link>
                    </li>
                  </ul>
                </Section>
              )}
              {home.announcements.length > 0 && (
                <Section title="Duyurular" aside={<Link to="/duyurular">Tümü</Link>}>
                  <ul className="rows">
                    {home.announcements.map((a) => (
                      <li key={a.id} className="row">
                        <div className="row__main">
                          <Link to={`/duyurular/${a.id}`} className="row__title">
                            {a.pinned && <Icon name="flag" size={16} label="Sabitlenmiş" />} {a.title}
                          </Link>
                          <p className="row__detail">{a.excerpt}</p>
                        </div>
                        {a.requiresAck && !a.acknowledged && <Status tone="attention">Onayınız bekleniyor</Status>}
                      </li>
                    ))}
                  </ul>
                </Section>
              )}
            </>
          );
        }}
      </QueryView>
    </div>
  );
}

function Actions({ home }: { home: Home }) {
  const i = useI18n();
  return (
    <Section title="Yapmanız gerekenler" aside={home.actions.length ? <span className="muted">{home.actions.length} iş</span> : null}>
      {home.actions.length === 0 ? (
        <Empty title="Her şey yolunda.">Sizden beklenen bir iş yok. Yeni bir şey olduğunda burada ve bildirimlerde göreceksiniz.</Empty>
      ) : (
        <ol className="todo">
          {home.actions.map((a, idx) => (
            <li key={a.id} className={`todo__item${a.overdue ? " is-overdue" : ""}`}>
              <span className="todo__num" aria-hidden>
                {idx + 1}
              </span>
              <div className="todo__main">
                <p className="todo__title">
                  <Icon name={ACTION_ICON[a.kind]} size={18} /> {a.title}
                </p>
                {a.detail && <p className="todo__detail">{a.detail}</p>}
                <p className="todo__meta">
                  {a.dueDate && (
                    <span className={a.overdue ? "due due--overdue" : "due"}>
                      {a.overdue ? "Gecikti · " : "Son gün "}
                      {i.date(a.dueDate, "dayMonth")}
                    </span>
                  )}
                  {a.since && !a.dueDate && <span className={a.overdue ? "due due--overdue" : ""}>{i.since(a.since)} bekliyor</span>}
                </p>
              </div>
              <LinkButton to={a.link} variant={idx === 0 ? "primary" : "secondary"} size="sm" className="todo__cta">
                {ACTION_CTA[a.kind]}
              </LinkButton>
            </li>
          ))}
        </ol>
      )}
    </Section>
  );
}

function Tracked({ home }: { home: Home }) {
  const i = useI18n();
  if (home.tracked.length === 0) return null;
  return (
    <Section title="Süren işleriniz">
      <ul className="rows">
        {home.tracked.map((t) => (
          <li key={`${t.kind}:${t.id}`} className="row">
            <div className="row__main">
              <Status tone={leaveTone(t.status)} detail={t.waitingSince ? i.since(t.waitingSince) : undefined}>
                {t.statusLabel}
              </Status>
              <Link to={t.link} className="row__title">
                {t.title}
              </Link>
              <p className="row__detail">
                {t.subtitle}
                {t.waitingOn && (
                  <>
                    {" · "}
                    <strong>{t.waitingOn}</strong> yanıtını bekliyor
                  </>
                )}
              </p>
            </div>
          </li>
        ))}
      </ul>
    </Section>
  );
}

function Team({ home }: { home: Home }) {
  const i = useI18n();
  const team = home.team!;
  return (
    <Section title="Ekibiniz" aside={<Link to="/ekibim">Ekip takvimi</Link>}>
      {team.awayToday.length === 0 ? (
        <p>Bugün ekibinizden izinli kimse yok.</p>
      ) : (
        <ul className="people">
          {team.awayToday.map((a) => (
            <li key={a.person.id} className="people__item">
              <Avatar initials={a.person.initials} size={32} />
              <span>
                {a.person.name}
                <span className="muted"> · {i.date(a.until, "dayMonth")} tarihine kadar izinli</span>
              </span>
            </li>
          ))}
        </ul>
      )}
      <p className="muted">
        Bu hafta {team.awayThisWeek} kişi izinli · ekip {team.size} kişi
      </p>
    </Section>
  );
}

function HrSummary({ hr }: { hr: NonNullable<Home["hr"]> }) {
  const i = useI18n();
  const items: { to: string; label: string; n: number }[] = [
    { to: "/onaylar", label: "İK onayı bekleyen talep", n: hr.pendingHrApprovals },
    { to: "/ik/belgeler", label: "İncelenecek belge", n: hr.documentsToReview },
    { to: "/ik/eksikler", label: "Eksik evrak", n: hr.missingDocuments },
    { to: "/ik/surecler", label: "Süren işe giriş", n: hr.onboardingsOpen },
    { to: "/ik/surecler", label: "Süren işten çıkış", n: hr.offboardingsOpen },
    { to: "/ik/calisanlar", label: "Kabul edilmemiş davet", n: hr.pendingInvitations },
  ];
  return (
    <Section title="İK masası">
      <ul className="counts">
        {items.map((x) => (
          <li key={x.label}>
            <Link to={x.to} className={`count${x.n > 0 ? " count--active" : ""}`}>
              <span className="count__n">{x.n}</span>
              <span className="count__label">{x.label}</span>
            </Link>
          </li>
        ))}
      </ul>
      {hr.startingSoon.length > 0 && (
        <>
          <h3 className="subhead">Yakında başlayanlar</h3>
          <ul className="people">
            {hr.startingSoon.map((s) => (
              <li key={s.person.id} className="people__item">
                <Avatar initials={s.person.initials} size={32} />
                <Link to={`/ik/calisanlar/${s.person.id}`}>{s.person.name}</Link>
                <span className="muted"> · {i.date(s.startDate, "dayMonth")}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </Section>
  );
}
